import {DetailsField, DetailsFieldsSection} from '@/components/ui/details-atoms/DetailsAtoms'
import {useTranslation} from '@/translations/i18n'
import {useMemo} from 'react'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Text} from '@/components/ui/text/Text'
import {generatePath, useOutletContext} from 'react-router-dom'
import {ButtonLink} from '@/components/ui/button-link/ButtonLink'
import {getPatientServiceType, patientServiceTypeMapToLabel} from '@/utilities/helpers'
import {FemaleIcon, MaleIcon} from '@/components/ui/icon/Icon'
import {useTheme} from 'styled-components'
import {routes} from '@/utilities/constants/routes'
import {Grid} from '@/components/ui/grid-atoms/GridAtoms'
import {ProjectExtended} from '@/features/projects/types'
import {livingConditionsToLabel, rehabilitationFacilitiesMapToLabel} from '@/features/projects/utils'
import {ProjectDetailsPatientEditDialog} from '@/features/projects/components/project-details-patient-edit-dialog/ProjectDetailsPatientEditDialog'
import {WarningLabel} from '@/components/ui/warning-label/WarningLabel'
import {usePermissions} from '@/features/auth/store/store'

export const ProjectDetailsPatientSection = () => {
    const {t} = useTranslation()
    const {project, isEditable} = useOutletContext<{project: ProjectExtended; isEditable: boolean}>()
    const {palette} = useTheme()
    const permissions = usePermissions()
    const yearsMonthsServiceType = useMemo(
        () => (project.patient.birthDate ? getPatientServiceType(new Date(project.patient.birthDate)) : null),
        [project.patient.birthDate]
    )

    // take the patient id from the snapshot first, if not present, take the current patient id
    const patientId = useMemo(
        () => project.patient.patientId ?? project.patient.id,
        [project.patient.patientId, project.patient.id]
    )

    return (
        <DetailsFieldsSection
            variant="primary"
            title={t('projects:patient_info')}
            headingEndSlot={isEditable && <ProjectDetailsPatientEditDialog project={project} />}
        >
            <Flexbox gap={3} fullWidth direction="column">
                <DetailsField fullWidth direction="column">
                    <Flexbox gap={1}>
                        {permissions?.patients?.read ? (
                            <ButtonLink
                                variant="tertiary"
                                shape="text"
                                to={generatePath(routes.PATIENT_DETAILS_ROUTE.handle.path, {
                                    id: patientId
                                })}
                            >
                                {project.patient.fullName}
                            </ButtonLink>
                        ) : (
                            <Text size="textSm" weight="500">
                                {project.patient.fullName}
                            </Text>
                        )}
                        {project.patient.gender == 'male' && <MaleIcon size={18} color={palette.primary[600]} />}
                        {project.patient.gender == 'female' && <FemaleIcon size={18} color={palette.purple[600]} />}
                    </Flexbox>
                    {yearsMonthsServiceType && (
                        <Text palette="neutral" shade="950" size="textSm" weight="500">
                            {t(patientServiceTypeMapToLabel[project.patient.serviceType])} (
                            {yearsMonthsServiceType.years > 0
                                ? `${t('commons:years_age', {count: yearsMonthsServiceType.years})}`
                                : `${t('commons:months_age', {count: yearsMonthsServiceType.months})}`}
                            )
                        </Text>
                    )}
                </DetailsField>
                <Grid columns={2} gap={3} fullWidth>
                    <DetailsField fullWidth>
                        <Text palette="neutral" shade="500" size="textSm">
                            {t('commons:residence')}
                        </Text>
                        <Text palette="neutral" shade="950" size="textSm" weight="500">
                            {`${project.patient.residence.address} ${project.patient.residence.streetNumber}, ${project.patient.residence.city.name} (${project.patient.residence.city.provinceAcronym}) ${project.patient.residence.cap}`}
                        </Text>
                    </DetailsField>
                    {project.patient.domicile && (
                        <DetailsField fullWidth>
                            <Text palette="neutral" shade="500" size="textSm">
                                {t('commons:domicile')}
                            </Text>
                            {project.patient.domicile.id == project.patient?.residence?.id ? (
                                <Text palette="neutral" shade="500" size="textSm">
                                    {t('commons:same_as_residence')}
                                </Text>
                            ) : (
                                <>
                                    <Text weight="500" size="textMd">
                                        {`${project.patient.domicile.address} ${project.patient.domicile.streetNumber}, ${project.patient.domicile.city.name} (${project.patient.domicile.city.provinceAcronym}) ${project.patient.domicile.cap}`}
                                    </Text>
                                    <Text palette="neutral" shade="500" size="textSm">
                                        {project.patient?.domicileAslDistrict?.aslName ?? t('commons:out_region')}
                                    </Text>
                                </>
                            )}
                        </DetailsField>
                    )}
                </Grid>
                <Grid columns={2} gap={3} fullWidth>
                    <DetailsField fullWidth>
                        <Text palette="neutral" shade="500" size="textSm">
                            {t('projects:residence_asl_district')}
                        </Text>
                        <Text palette="neutral" shade="950" size="textSm" weight="500">
                            {project.patient.residenceAslDistrict?.aslName} -{' '}
                            {project.patient.residenceAslDistrict?.name}
                        </Text>
                    </DetailsField>
                    <DetailsField fullWidth>
                        <Text palette="neutral" shade="500" size="textSm">
                            {t('projects:domicile_asl_district')}
                        </Text>
                        <Text palette="neutral" shade="950" size="textSm" weight="500">
                            {project.patient.domicileAslDistrict?.id == project.patient.residenceAslDistrict?.id ? (
                                <Text render="span" palette="neutral" shade="500" size="textSm">
                                    {t('commons:same_as_residence')}
                                </Text>
                            ) : (
                                `${project.patient.domicileAslDistrict?.aslName} - ${project.patient.domicileAslDistrict?.name}`
                            )}
                        </Text>
                    </DetailsField>
                </Grid>
                <Grid columns={2} gap={3} fullWidth>
                    <DetailsField fullWidth>
                        <Text palette="neutral" shade="500" size="textSm">
                            {t('projects:life_conditions')}
                        </Text>
                        <Text palette="neutral" shade="950" size="textSm" weight="500">
                            {project.livingConditions ? (
                                t(livingConditionsToLabel[project.livingConditions])
                            ) : (
                                <WarningLabel text={t('users:to_compile')} />
                            )}
                        </Text>
                    </DetailsField>
                    <DetailsField fullWidth>
                        <Text palette="neutral" shade="500" size="textSm">
                            {t('projects:hospital_rehabilitation_last_12_months')}
                        </Text>
                        <Text palette="neutral" shade="950" size="textSm" weight="500">
                            {project.rehabilitationFacility ? (
                                t(rehabilitationFacilitiesMapToLabel[project.rehabilitationFacility])
                            ) : (
                                <WarningLabel text={t('users:to_compile')} />
                            )}
                        </Text>
                    </DetailsField>
                </Grid>
            </Flexbox>
        </DetailsFieldsSection>
    )
}
